import { get, post, put, del } from '../client/axiosClient';
import { engagementEndpoints } from '../endpoints/engagement.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, PaginatedResponse, EmptyResponse, ID,
  Engagement, CreateEngagementRequest, UpdateEngagementRequest, EngagementSearchRequest,
  ClosurePreview, CloseEngagementRequest, ReopenEngagementRequest, EngagementClosure,
} from '../types';

class EngagementService {
  async getAll(params?: EngagementSearchRequest): Promise<PaginatedResponse<Engagement>> {
    const endpoint = params
      ? buildEndpointWithQuery(engagementEndpoints.basePath(), params as Record<string, any>)
      : engagementEndpoints.list();
    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Engagement>> {
    const response = await get<ApiResponse<Engagement>>(engagementEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateEngagementRequest): Promise<ApiResponse<Engagement>> {
    const response = await post<ApiResponse<Engagement>>(engagementEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateEngagementRequest): Promise<ApiResponse<Engagement>> {
    const response = await put<ApiResponse<Engagement>>(engagementEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(engagementEndpoints.delete(id));
    return { success: true };
  }

  // ------------------------------------------------------------------------
  // Closure / Reopen
  // ------------------------------------------------------------------------

  async getClosurePreview(id: ID): Promise<ClosurePreview | null> {
    const response = await get<ApiResponse<ClosurePreview>>(engagementEndpoints.closurePreview(id));
    return response.data?.data ?? null;
  }

  async close(id: ID, data: CloseEngagementRequest): Promise<ApiResponse<Engagement>> {
    const response = await post<ApiResponse<Engagement>>(engagementEndpoints.close(id), data);
    return response.data;
  }

  async reopen(id: ID, data: ReopenEngagementRequest): Promise<ApiResponse<Engagement>> {
    const response = await post<ApiResponse<Engagement>>(engagementEndpoints.reopen(id), data);
    return response.data;
  }

  async getClosureHistory(id: ID): Promise<EngagementClosure[]> {
    const response = await get<ApiResponse<EngagementClosure[]>>(engagementEndpoints.closureHistory(id));
    return response.data?.data ?? [];
  }
}

export const engagementService = new EngagementService();
export default engagementService;
